import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useMap } from "../../hooks/useMap";
import { useActivity } from "../../hooks/useActivity";
import { usePreferences } from "../../hooks/usePreferences";
import { useQuickIsland } from "../../contexts/QuickIslandContext";
import { UserMenu } from "../Auth/UserMenu";
import { CompassRose } from "../Map/CompassRose";
import { getMapStyleInfo } from "../../config/mapStyles";
import { getCameraService } from "../../services/cameraService";
import debugx from "debugx";

const debug = debugx("bikedash:header");

export function Header() {
  const { user } = useAuth();
  const { currentMapStyle, cycleMapStyle, starredStyles, isMapLoading } =
    useMap();
  const { currentPosition } = useActivity();
  const { mapPreferences, updateMapPreferences, userPreferences } =
    usePreferences();
  const { openIsland } = useQuickIsland();
  const [showSettings, setShowSettings] = useState(false);
  const [mapService, setMapService] = useState<any>(null);
  const [isPressed, setIsPressed] = useState(false);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTriggeredRef = useRef(false);
  const touchStartY = useRef<number>(0);
  const swipeDetectedRef = useRef(false);
  const isTouchInteractionRef = useRef(false);
  const mapButtonRef = useRef<HTMLButtonElement>(null);
  const cameraButtonRef = useRef<HTMLButtonElement>(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraLabel, setCameraLabel] = useState("Camera");
  const [isBtConnected, setIsBtConnected] = useState(false);
  const lastCameraCycleTimeRef = useRef<number>(0);

  useEffect(() => {
    // Get map service for compass
    const service = window.__bikedash_maps?.["main"];
    if (service) {
      setMapService(service);
    }

    // Check initial camera state and update label
    const updateCameraState = () => {
      const cameraService = getCameraService();
      const settings = cameraService.getSettings();
      setCameraEnabled(cameraService.isEnabled());
      setIsBtConnected(cameraService.isBluetoothConnected());

      if (!settings.active) {
        setCameraLabel("Off");
      } else if (settings.type === "bluetooth") {
        setCameraLabel("🔵 BT");
      } else if (settings.facingMode === "user") {
        setCameraLabel("🤳 Front");
      } else {
        setCameraLabel("📱 Back");
      }
    };

    updateCameraState();

    // Poll for changes
    const interval = setInterval(updateCameraState, 500);

    return () => clearInterval(interval);
  }, []);

  // Native event listener to prevent pull-to-refresh without breaking React
  useEffect(() => {
    const button = mapButtonRef.current;
    if (!button) return;

    let startY = 0;

    const handleNativeTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const swipeDistance = currentY - startY;

      // Prevent pull-to-refresh when swiping down
      if (swipeDistance > 0) {
        e.preventDefault();
      }
    };

    // Use non-passive listeners so we can preventDefault
    button.addEventListener("touchstart", handleNativeTouchStart, {
      passive: false,
    });
    button.addEventListener("touchmove", handleNativeTouchMove, {
      passive: false,
    });

    return () => {
      button.removeEventListener("touchstart", handleNativeTouchStart);
      button.removeEventListener("touchmove", handleNativeTouchMove);
    };
  }, []);

  const handlePressStart = () => {
    longPressTriggeredRef.current = false;
    setIsPressed(true);

    pressTimerRef.current = setTimeout(() => {
      debug("Long press detected, opening map detail island");
      longPressTriggeredRef.current = true;
      openIsland("map-styles");
    }, 500);
  };

  const handlePressEnd = () => {
    setIsPressed(false);

    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }

    // Only cycle if it wasn't a long press or swipe
    if (!longPressTriggeredRef.current && !swipeDetectedRef.current) {
      debug("Short press - cycling map style");
      cycleMapStyle();
    }

    // Reset refs immediately
    longPressTriggeredRef.current = false;
    swipeDetectedRef.current = false;
  };

  const handlePressCancel = () => {
    setIsPressed(false);
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    longPressTriggeredRef.current = false;
    swipeDetectedRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const swipeDistance = currentY - touchStartY.current;
    const swipeThreshold = 30; // pixels

    // Don't call preventDefault here - it breaks React's event system!
    // The native listener will handle preventing pull-to-refresh

    // If swiped down more than threshold, open map-styles
    if (swipeDistance > swipeThreshold && !swipeDetectedRef.current) {
      swipeDetectedRef.current = true;
      longPressTriggeredRef.current = true; // Prevent cycling
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
      }
      debug("Swipe down detected, opening map-styles island");
      openIsland("map-styles");
    }
  };

  const handleCameraClick = async () => {
    const cameraService = getCameraService();
    const currentSettings = cameraService.getSettings();
    // Check if BT is connected (regardless of current camera type)
    const isBtConnected = cameraService.isBluetoothConnected();

    const now = Date.now();
    const timeSinceLastCycle = now - lastCameraCycleTimeRef.current;

    // If more than 3 seconds since last cycle and camera is on, turn it off
    if (timeSinceLastCycle > 3000 && currentSettings.active) {
      debug("Camera timeout (>3s since last cycle) → switching to OFF");
      await cameraService.stop();
      setCameraLabel("Off");
      lastCameraCycleTimeRef.current = now;
      setCameraEnabled(cameraService.isEnabled());
      return;
    }

    // Cycle through modes:
    // If BT connected: OFF → BT → Front → Back → OFF
    // If BT not connected: OFF → Front → Back → OFF
    if (!currentSettings.active) {
      // Currently OFF → switch to BT if connected, otherwise Front
      if (isBtConnected) {
        debug("Camera OFF → switching to BT");
        await cameraService.updateSettings({ type: "bluetooth" });
        await cameraService.start();
        setCameraLabel("🔵 BT");
      } else {
        debug("Camera OFF → switching to Front");
        await cameraService.updateSettings({
          type: "phone",
          facingMode: "user",
        });
        await cameraService.start();
        setCameraLabel("🤳 Front");
      }
    } else if (currentSettings.type === "bluetooth") {
      // Currently BT → switch to Front
      debug("Camera BT → switching to Front");
      await cameraService.updateSettings({ type: "phone", facingMode: "user" });
      await cameraService.start();
      setCameraLabel("🤳 Front");
    } else if (
      currentSettings.type === "phone" &&
      currentSettings.facingMode === "user"
    ) {
      // Currently Front → switch to Back
      debug("Camera Front → switching to Back");
      await cameraService.updateSettings({ facingMode: "environment" });
      await cameraService.start();
      setCameraLabel("📱 Back");
    } else if (
      currentSettings.type === "phone" &&
      currentSettings.facingMode === "environment"
    ) {
      // Currently Back → switch to OFF
      debug("Camera Back → switching to OFF");
      await cameraService.stop();
      setCameraLabel("Off");
    }

    // Update the last cycle time
    lastCameraCycleTimeRef.current = now;
    setCameraEnabled(cameraService.isEnabled());
  };

  const handleCameraLongPress = () => {
    debug("Camera long press detected, opening camera controls");
    openIsland("camera-controls");
  };

  const handleCameraSwipeDown = () => {
    debug("Camera swipe down detected, opening camera controls");
    openIsland("camera-controls");
  };

  return (
    <header className="header">
      <div style={{ position: "relative" }}>
        <button
          ref={mapButtonRef}
          className={`map-cycle-btn ${isMapLoading ? "loading" : ""}`}
          style={{ touchAction: "pan-x" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Don't handle click - we handle everything in mouse/touch events
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            // Ignore mouse events if touch was used
            if (isTouchInteractionRef.current) return;
            handlePressStart();
          }}
          onMouseUp={(e) => {
            e.preventDefault();
            // Ignore mouse events if touch was used
            if (isTouchInteractionRef.current) return;
            handlePressEnd();
          }}
          onMouseLeave={(e) => {
            e.preventDefault();
            // Ignore mouse events if touch was used
            if (isTouchInteractionRef.current) return;
            handlePressCancel();
          }}
          onTouchStart={(e) => {
            // Don't preventDefault - let native listener handle it
            isTouchInteractionRef.current = true;
            touchStartY.current = e.touches[0].clientY;
            swipeDetectedRef.current = false;
            handlePressStart();
          }}
          onTouchMove={(e) => {
            handleTouchMove(e);
          }}
          onTouchEnd={() => {
            // Don't preventDefault - let React handle click events properly
            handlePressEnd();
            // Reset touch flag after synthetic mouse events would have fired
            setTimeout(() => {
              isTouchInteractionRef.current = false;
            }, 500);
          }}
          onTouchCancel={() => {
            // Don't preventDefault - let React handle click events properly
            handlePressCancel();
            // Reset touch flag after synthetic mouse events would have fired
            setTimeout(() => {
              isTouchInteractionRef.current = false;
            }, 500);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
          }}
        >
          <span className="map-icon">
            {currentMapStyle
              ? getMapStyleInfo(currentMapStyle.id)?.icon || "🗺️"
              : "🗺️"}
          </span>
          <span className="map-name">
            {currentMapStyle?.name || "Map"}
            {mapPreferences.satelliteUnderlay && " / 🌍"}
          </span>
          {isMapLoading && (
            <div
              className="status-dot pulse"
              style={{
                background: "var(--accent)",
                left: "-2px",
                right: "auto",
              }}
            />
          )}
        </button>
      </div>

      {userPreferences.cameraEnabled && (
        <div
          style={{
            position: "absolute",
            top: "52px",
            left: "16px",
          }}
        >
          <button
            ref={cameraButtonRef}
            className={`map-cycle-btn ${cameraEnabled ? "active" : ""}`}
            onClick={handleCameraClick}
            onMouseDown={(e) => {
              e.preventDefault();
              if (isTouchInteractionRef.current) return;
              const timer = setTimeout(handleCameraLongPress, 500);
              const cleanup = () => {
                clearTimeout(timer);
                window.removeEventListener("mouseup", cleanup);
              };
              window.addEventListener("mouseup", cleanup);
            }}
            onTouchStart={(e) => {
              isTouchInteractionRef.current = true;
              touchStartY.current = e.touches[0].clientY;
              const timer = setTimeout(handleCameraLongPress, 500);
              const cleanup = () => {
                clearTimeout(timer);
              };
              cameraButtonRef.current?.addEventListener("touchend", cleanup, {
                once: true,
              });
            }}
            onTouchMove={(e) => {
              const currentY = e.touches[0].clientY;
              const swipeDistance = currentY - touchStartY.current;
              if (swipeDistance > 30) {
                handleCameraSwipeDown();
              }
            }}
            onTouchEnd={() => {
              setTimeout(() => {
                isTouchInteractionRef.current = false;
              }, 500);
            }}
            style={{
              touchAction: "pan-x",
            }}
          >
            <span className="map-icon">📷</span>
            <span className="map-name">{cameraLabel}</span>
            {isBtConnected && (
              <div
                className="status-dot"
                style={{
                  background: "#3b82f6",
                  left: "-2px",
                  bottom: "-2px",
                  right: "auto",
                  top: "auto",
                }}
              />
            )}
          </button>
        </div>
      )}

      <div className="header-right">
        <UserMenu user={user} />
        <CompassRose
          mapService={mapService}
          currentPosition={currentPosition || undefined}
        />
      </div>
    </header>
  );
}
